import {
  AspectRatio,
  Box,
  Flex,
  Skeleton,
  SkeletonCircle,
  Stack,
  Text,
} from "@chakra-ui/react";
import Head from "next/head";
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { DetailData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";
import CustomWrapper from "components/Chakra/CustomWrapper";
import LogoIdealniNajemce from "components/Chakra/Icons/LogoIdealninajemce";
import { MediaGallery } from "components/Chakra/MediaGallery";
import MetaHead from "components/MetaHead";
import { useFlagsmith } from "contexts/Flagsmith";
import StoryblokTypeUserProvider from "contexts/StoryblokTypeUser";
import { useTracking } from "contexts/Tracking";
import { createContext } from "helpers/createContext";
import useGlobalState from "hooks/useGlobalState";
import useHelperCRM from "hooks/useHelperCRM";
import useRouter from "hooks/useRouter";
import { useAlertModal } from "modals/useAlertModal";
import { useContactModal } from "modals/useContactModal";
import { EOfferStatus, IPhoto } from "utils/offer";
import { EOrderTypeId } from "utils/payment/types";
import { addToHistory } from "utils/search/history";
import ActionButtons from "./components/ActionButtons";
import BreadcrumbNavigation from "./components/BreadcrumbNavigation";
import Contact from "./components/Contact";
import Description from "./components/Description";
import EditOwn from "./components/EditOwn";
import InactiveMessage, { TInactiveType } from "./components/InactiveMessage";
import INContainer from "./components/INContainer";
import KeyValueInfo from "./components/KeyValueInfo";
import Locality from "./components/Locality";
import Neighborhood from "./components/Neighborhood";
import Perex from "./components/Perex";
import Pricing from "./components/Pricing";
import StickyContact from "./components/StickyContact";
import WatchdogBanner from "./components/WatchdogBanner";
import RelatedOffers from "./components/RelatedOffers";
import { HypoWidget } from "components/HypoWidget";
import useViewport from "hooks/useViewport";

interface IProps {
  offer?: DetailData;
  isFallback: boolean;
  blogComponents: ReactNode | null;
}

const noApartmentIds = [5296691, 5296638, 5296637, 5248162];

export const isIncludeApartment = (id: number): boolean =>
  noApartmentIds.includes(id);

const getMeta = (offer: DetailData): JSX.Element | null => {
  if (!offer) {
    return null;
  }

  const getTitle = (): string => {
    let title = offer.title;

    title += ` ${offer?.street?.name}, ${offer?.village?.name} `;

    if (offer.villagePart) {
      title += ` - ${offer.villagePart.name}`;
    }

    if (offer.isNoCommission) {
      title += " - bez provize";
    } else {
      title += ` - ${offer.rentalPrice?.value} ${offer.rentalPrice?.currency} `;
    }

    return title;
  };

  const getDescription = (): string => {
    let description = offer.title;

    description += `  ${offer?.street?.name}, ${offer?.village?.name}`;

    if (offer.villagePart) {
      description += ` - ${offer.villagePart.name}`;
    }

    if (offer.isNoCommission) {
      description += " bez provize";
    }

    description += ` za ${offer.rentalPrice?.value} ${offer.rentalPrice?.currency} .Hledejte nabídky snadno a rychle na stránce s největší nabídkou pronájmů.`;

    return description;
  };

  return (
    <MetaHead
      title={getTitle()}
      description={getDescription()}
      canonicalUrl={offer.absoluteUrl}
      imageUrl={
        offer?.photos && offer?.photos?.length > 0
          ? offer.photos[0].path.toString()
          : undefined
      }
    />
  );
};

type DetailStateProps = {
  offer?: DetailData;
  id?: number;
  status?: DetailData["status"];
  isLoaded?: boolean;
};

const [DetailStateProvider, useDetailState] = createContext<DetailStateProps>({
  name: "DetailState",
});

export { useDetailState };

const OfferDetail: React.FC<IProps> = ({
  offer,
  isFallback,
  blogComponents,
}) => {
  const [isInViewport, setIsInViewport] = useState(false);

  const { isMobile } = useViewport();

  const { trackEvent, trackPage } = useTracking();
  const { user } = useGlobalState();
  const { query, asPath } = useRouter();
  const { updateUserAfterPayment } = useHelperCRM();
  // const { isCreated } = useExitWatchDog();
  // const { openModal: openWatchdogExitModal, modals } = useWatchdogExitModal();
  const { getFlagByName } = useFlagsmith();

  const flag = getFlagByName("offer_detail_blog");

  const isFlagAlternative =
    flag && flag?.enabled && flag?.value === "offer_detail_with_blog";

  const [, , , urlOfferId] = asPath.split(/[/?]+/);
  // const showWatchdogExitModal =
  //   isBrowser() && !Boolean(sessionStorage.getItem("WATCHDOG_MODAL_CLOSED"));

  const { openModal: openAlertModal } = useAlertModal();
  const { openModal: openContactModal } = useContactModal();

  const observer = useRef<IntersectionObserver>();
  const observerRef = useCallback((node: Element | null) => {
    if (observer.current) {
      observer.current.disconnect();
    }
    observer.current = new IntersectionObserver((entries) =>
      setIsInViewport(entries[0].isIntersecting),
    );

    if (node) {
      observer.current.observe(node);
    }
  }, []);

  useEffect(() => {
    if (offer?.id) {
      addToHistory(offer.id.toString());
    }
  }, [offer?.id]);

  useEffect(() => {
    if (offer) {
      trackEvent({
        "dl.eCat": "hledání",
        "dl.eAct": "zobrazeni_inzeratu",
        "dl.eLab": offer?.id?.toString() || "",
      });
      trackPage({
        offerOwner: offer?.owner?.type || "Soukromý pronajímatel",
        offerStatus:
          offer.status === EOfferStatus.ACTIVE ? "active" : "inactive",
      });
    }
  }, [offer]);

  useEffect(() => {
    if (query.platba) {
      if (query.platba === "nezaplacena") {
        if (Number(query.orderType) === EOrderTypeId.IMMENDIATE_PHONE) {
          return openAlertModal({
            animation: "error",
            title: "Platbu se bohužel nepodařilo uskutečnit.",
            children:
              "Zkuste to prosím znovu, nebo nás kontaktujte na info@ulovdomov.cz.",
            submitText: "Zkusit znovu",
          });
        }

        return openAlertModal({
          animation: "error",
          title: "Premium účet se nepodařilo aktivovat",
          children:
            "Platbu se bohužel nepodařilo uskutečnit. Zkuste to prosím znovu, nebo nás kontaktujte na info@ulovdomov.cz.",
          submitText: "Zkusit znovu",
        });
      }

      if (Number(query.orderType) === EOrderTypeId.IMMENDIATE_PHONE) {
        openContactModal({
          offerId: parseInt(urlOfferId),
          isSuspicious: offer?.showScamWarning,
          loginEvent: {
            LOGIN: {
              "dl.eAct": "login",
              "dl.eLab": "Odeslání zprávy – detail inzerátu",
              "dl.eCat": "",
            },
            REGISTRATION: {
              "dl.eAct": "registration",
              "dl.eLab": "Odeslání zprávy – detail inzerátu",
              "dl.eCat": "",
            },
          },
        });

        return openAlertModal({
          animation: "check",
          title: "Můžete okamžitě kontaktovat po celý týden",
          submitText: "Děkuji",
          closeOnlyAlertModalOnClose: true,
        });
      }

      const orderType = query.orderType;
      const isPremiumPayment =
        !orderType ||
        [
          EOrderTypeId.PREMIUM_ACC_6_MONTHS,
          EOrderTypeId.PREMIUM_ACC_12_MONTHS,
          EOrderTypeId.PREMIUM_ACC_UNLIMITED,
        ].includes(Number(orderType));

      if (!isPremiumPayment) {
        return;
      }

      return openAlertModal({
        animation: "check",
        title: "Stali jste se prémiovým uživatelem",
        children:
          "Využijte prémiové výhody naplno. Hledejte pronájmy bez provize nebo si nastavte instantního Hlídacího psa.",
        submitText: "Paráda",
      });
    }
  }, []);

  useEffect(() => {
    if (user && query.platba === "zaplacena") {
      const orderType = query.orderType;
      const isPremiumPayment =
        !orderType ||
        [
          EOrderTypeId.PREMIUM_ACC_6_MONTHS,
          EOrderTypeId.PREMIUM_ACC_12_MONTHS,
          EOrderTypeId.PREMIUM_ACC_UNLIMITED,
        ].includes(Number(orderType));

      if (!isPremiumPayment) {
        return;
      }

      updateUserAfterPayment({
        user,
        orderType: query.orderType,
      });
    }
  }, [user]);

  useEffect(() => {
    if (typeof isFlagAlternative === "boolean") {
      trackEvent({
        "dl.eCat": "offer_detail",
        "dl.eAct": "ab.participated",
        "dl.eLab": isFlagAlternative
          ? "offer_detail_with_blog"
          : "original_offer_detail_without_blog",
      });
    }
  }, [isFlagAlternative]);

  const { inactiveType, myOwn, offerStatus } = useMemo(() => {
    const memoizedOfferStatus = offer?.status;
    let memoizedInactiveType: TInactiveType | undefined;
    let memoizedMyOwn = false;

    if (!offer) {
      return {
        inactiveType: memoizedInactiveType,
        myOwn: memoizedMyOwn,
        offerStatus: memoizedOfferStatus,
      };
    }

    if (
      offer.status !== EOfferStatus.ACTIVE &&
      offer.status !== EOfferStatus.IMPORT_ACTIVE
    ) {
      memoizedInactiveType =
        user && user.id === offer?.owner?.id ? "owner" : "customer";
    }

    if (user) {
      memoizedMyOwn = user.id === offer?.owner?.id;
    }

    return {
      inactiveType: memoizedInactiveType,
      myOwn: memoizedMyOwn,
      offerStatus: memoizedOfferStatus,
    };
  }, [offer, user]);

  const getIntroPhoto = (): IPhoto => {
    if (offer?.photos?.length) {
      return offer.photos[0];
    }

    return {
      id: 1,
      path: "/fe/img/common/inzerat_bez_fotky.jpg",
    };
  };

  const getGalleryItems = () => {
    if (offer?.photos?.length) {
      return offer.photos;
    }

    return [
      {
        id: 1,
        path: "/fe/img/common/inzerat_bez_fotky.jpg",
      },
    ];
  };

  const InfoINContainer = (): JSX.Element => (
    <Stack
      direction="row"
      backgroundColor="ud-grey-150"
      justifyContent="center"
      p="20px 16px"
    >
      <Text>Bezstarostné bydlení se službou</Text>
      <Stack justify="end">
        <LogoIdealniNajemce h="23px" w="auto" fill="ud-primary" />
      </Stack>
    </Stack>
  );

  return (
    <>
      <Head>
        <link
          href="https://unpkg.com/maplibre-gl@2.4.0/dist/maplibre-gl.css"
          rel="stylesheet"
        />
      </Head>
      <DetailStateProvider
        value={{
          offer,
          status: offerStatus,
          id: offer?.id,
          isLoaded: !isFallback,
        }}
      >
        {offer && getMeta(offer)}
        <Box display={{ base: "none", md: "block" }}>
          <CustomWrapper maxW="1210px">
            <BreadcrumbNavigation
              py="16px"
              pl={["16px", "unset"]}
              isFallback={!isFallback}
              offer={offer}
            />
            <Box>
              {inactiveType && (
                <InactiveMessage type={inactiveType} mb="30px" />
              )}
              <Flex mb="24px">
                <Box mr={8} w="100%">
                  <Box mb={8}>
                    {!isFallback && offer ? (
                      <MediaGallery
                        isDetail
                        streetViewButton={
                          offerStatus === EOfferStatus.ACTIVE &&
                          offer?.geoCoordinates
                            ? [
                                offer?.geoCoordinates?.lat,
                                offer?.geoCoordinates?.lng,
                              ]
                            : false
                        }
                        media={getGalleryItems()}
                        introPhoto={getIntroPhoto()}
                        // isVideo={Boolean(offer.video)} // ---- PRODEJE ----
                        // matterportURl={offer.matterport_url} // ---- PRODEJE ----
                        data-test="offerDetail.photoGallery"
                      />
                    ) : (
                      <AspectRatio
                        maxW="100%"
                        width="100%"
                        maxH="100%"
                        height="100%"
                        ratio={4 / 3}
                        position="relative"
                        cursor="pointer"
                      >
                        <Skeleton />
                      </AspectRatio>
                    )}
                  </Box>
                  <Description mb="60px" />
                  <KeyValueInfo
                    mb="60px"
                    data-test="offerDetail.keyValueInfo"
                  />
                  <Locality
                    mapID="1"
                    mb="60px"
                    data-test="offerDetail.locality"
                  />
                  <Neighborhood mb="60px" />
                </Box>
                <Stack
                  data-test="offerDetail.rightPanel"
                  flex="0 0 370px"
                  position="relative"
                >
                  <Box position="sticky" top="80px">
                    {offerStatus === EOfferStatus.ACTIVE && myOwn && (
                      <EditOwn />
                    )}
                    <Perex mb="16px" />
                    {!Boolean(inactiveType) && <Pricing mb="20px" />}{" "}
                    {!isFallback && offer ? (
                      <>
                        {offer?.owner?.isIn ? (
                          <INContainer myOwn={myOwn} mb="20px" />
                        ) : (
                          <Contact mb="20px" />
                        )}
                      </>
                    ) : (
                      <Stack
                        direction="column"
                        borderColor="ud-grey-100"
                        borderWidth="1px"
                        borderRadius="6px"
                        padding="16px"
                        h="150px"
                        spacing="24px"
                      >
                        <Stack direction="row">
                          <SkeletonCircle boxSize="50px" />
                          <Stack>
                            <Skeleton h="20px" w="200px" />
                            <Stack
                              direction="row"
                              spacing={5}
                              align="center"
                              justifyContent="center"
                            >
                              <Skeleton h="15px" w="115px" />
                              <Skeleton h="15px" w="115px" />
                            </Stack>
                          </Stack>
                        </Stack>
                        <Skeleton h="35px" w="310px" borderRadius="6px" />
                      </Stack>
                    )}
                    {!isFallback ? (
                      <>
                        {offerStatus === EOfferStatus.ACTIVE && (
                          <>
                            {offer?.offerTypeId === "coliving" ||
                              (offer?.offerTypeId === "rent" && (
                                <WatchdogBanner data-test="offerDetail.watchdogBanner" />
                              ))}
                            <ActionButtons
                              mt="20px"
                              data-test="offerDetail.actionButtons"
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <Stack mt="20px">
                        <Skeleton h="60px" w="370px" />
                        <Stack direction="row">
                          <Skeleton h="20px" w="190px" />
                          <Skeleton h="20px" w="190px" />
                          <Skeleton h="20px" w="190px" />
                        </Stack>
                      </Stack>
                    )}
                  </Box>
                </Stack>
              </Flex>
            </Box>
          </CustomWrapper>
        </Box>

        <Box display={{ base: "block", md: "none" }}>
          <BreadcrumbNavigation isFallback={!isFallback} offer={offer} />
          {inactiveType && <InactiveMessage type={inactiveType} mb="30px" />}
          {!isFallback && offer ? (
            <>{offer?.owner?.isIn && <InfoINContainer />}</>
          ) : (
            <Skeleton />
          )}

          <CustomWrapper>
            <Box mb={8}>
              {!isFallback && offer ? (
                <MediaGallery
                  isDetail
                  streetViewButton={
                    offerStatus === EOfferStatus.ACTIVE && offer?.geoCoordinates
                      ? [offer?.geoCoordinates?.lat, offer?.geoCoordinates?.lng]
                      : false
                  }
                  media={getGalleryItems()}
                  // isVideo={Boolean(JSON_MOCK_DATA.video)}
                  introPhoto={getIntroPhoto()}
                />
              ) : (
                <AspectRatio
                  maxW="100%"
                  width="100%"
                  maxH="100%"
                  height="100%"
                  ratio={4 / 3}
                  position="relative"
                  cursor="pointer"
                >
                  <Skeleton />
                </AspectRatio>
              )}
            </Box>
            <Perex mb="15px" />
            <Pricing mb="20px" />
            {offer?.offerTypeId === "coliving" ||
              (offer?.offerTypeId === "rent" && <WatchdogBanner mb="40px" />)}
            <Description mb="60px" />
            <KeyValueInfo mb="60px" />
            <Locality mapID="2" mb="60px" />
            <Neighborhood mb="60px" />
            <Box ref={observerRef} />
            {!isFallback && offer ? (
              <>
                {offer?.owner?.isIn ? (
                  <INContainer myOwn={myOwn} mb="40px" />
                ) : (
                  <Contact mb="40px" />
                )}
              </>
            ) : (
              <Stack
                direction="column"
                borderColor="ud-grey-100"
                borderWidth="1px"
                borderRadius="6px"
                padding="16px"
                h="150px"
                spacing="24px"
              >
                <Stack direction="row">
                  <SkeletonCircle boxSize="50px" />
                  <Stack>
                    <Skeleton h="20px" w="200px" />
                    <Stack
                      direction="row"
                      spacing={5}
                      align="center"
                      justifyContent="center"
                    >
                      <Skeleton h="15px" w="115px" />
                      <Skeleton h="15px" w="115px" />
                    </Stack>
                  </Stack>
                </Stack>
                <Skeleton h="35px" w="310px" borderRadius="6px" />
              </Stack>
            )}
            {!isFallback ? (
              <ActionButtons mb="20px" />
            ) : (
              <Stack direction="row" mt="20px">
                <Skeleton h="25px" w="190px" />
                <Skeleton h="25px" w="190px" />
                <Skeleton h="25px" w="190px" />
              </Stack>
            )}
          </CustomWrapper>
        </Box>
        <CustomWrapper
          stack={!isMobile}
          maxW={{ base: "unset", md: "1210px" }}
          bgColor="ud-grey-100"
          mt={{ base: "40px", md: "unset" }}
        >
          {offer?.offerTypeId === "sale" && <HypoWidget />}
          <RelatedOffers />
          <StoryblokTypeUserProvider>
            {isFlagAlternative && blogComponents}
          </StoryblokTypeUserProvider>
        </CustomWrapper>
        {offerStatus === EOfferStatus.ACTIVE && !isInViewport && isMobile && (
          <StickyContact />
        )}
      </DetailStateProvider>
    </>
  );
};

export default OfferDetail;
